import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { LoadingFallback } from '@/components/LoadingFallback';
import { useUser } from '@/contexts/UserContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  // Check if URL contains unprocessed auth tokens (from email verification)
  const hasAuthTokensInHash = () => {
    const hash = window.location.hash;
    if (!hash) return false;
    const params = new URLSearchParams(hash.substring(1));
    return params.has('access_token') || params.get('type') === 'signup';
  };

  // FIRST: Check sessionStorage for signup verification flag BEFORE any hooks run their effects
  // This must be the absolute first check to prevent any race conditions
  const isSignupVerificationFromStorage = sessionStorage.getItem('isSignupVerification') === 'true';
  
  // Define setup routes that should be accessible before setup is complete
  // NOTE: /pending-approval IS included here as it's part of the setup flow after onboarding
  const isSetupRoute = window.location.pathname.includes('/role-selection') || 
                      window.location.pathname.includes('/onboarding') ||
                      window.location.pathname.includes('/pending-approval') ||
                      window.location.pathname.includes('/reset-password') ||
                      window.location.pathname.includes('/setup-password');

  // IMMEDIATE REDIRECT: If signup verification flag is set, redirect to role-selection
  // This happens BEFORE any loading, auth checks, or profile loading
  if (isSignupVerificationFromStorage && !isSetupRoute) {
    console.log('ProtectedRoute: Signup verification flag detected in sessionStorage, redirecting to role-selection IMMEDIATELY');
    return <Navigate to="/role-selection" replace />;
  }

  const { user, loading: authLoading, isSignupVerification } = useSupabaseAuth();
  const { accountType, currentUser, betaAccess, setupCompleted, loading: userContextLoading } = useUser();
  const [contextReady, setContextReady] = useState(false);
  const [waitingForAuthTokens, setWaitingForAuthTokens] = useState(() => hasAuthTokensInHash());

  // Wait for auth tokens to be processed
  useEffect(() => {
    if (waitingForAuthTokens && user) {
      // User is now authenticated, tokens have been processed
      console.log('ProtectedRoute: Auth tokens processed, user authenticated');
      setWaitingForAuthTokens(false);
    } else if (waitingForAuthTokens && !hasAuthTokensInHash()) {
      // Hash was cleared but no user - tokens may have been invalid
      console.log('ProtectedRoute: Auth hash cleared');
      setWaitingForAuthTokens(false);
    }
  }, [user, waitingForAuthTokens]);

  // If we have auth tokens in hash and no user yet, wait for Supabase to process them
  if (waitingForAuthTokens && !user && !authLoading) {
    console.log('ProtectedRoute: Auth tokens in hash, waiting for Supabase to process');
    return <LoadingFallback />;
  }

  useEffect(() => {
    // Wait for UserContext to finish loading before deciding on redirects
    if (!authLoading && !userContextLoading) {
      setContextReady(true);
    }
  }, [authLoading, userContextLoading, accountType, currentUser]);

  // Show loading while checking auth and context
  if (authLoading || userContextLoading || !contextReady) {
    return <LoadingFallback />;
  }

  console.log('ProtectedRoute: Auth check complete', {
    hasUser: !!user,
    accountType,
    setupCompleted,
    betaAccess,
    isSetupRoute,
    currentPath: window.location.pathname,
    willRedirectToRoleSelection: !accountType && !isSetupRoute,
    willRedirectToOnboarding: !!accountType && !setupCompleted && !isSetupRoute,
    willRedirectToPending: !!setupCompleted && !betaAccess && !isSetupRoute
  });

  // Redirect to auth if not logged in
  // BUT: Don't redirect if we're waiting for auth tokens or if signup verification is in progress
  if (!user && !isSignupVerificationFromStorage && !waitingForAuthTokens) {
    return <Navigate to="/auth" replace />;
  }
  
  // If we're still waiting for user during signup verification, show loading
  if (!user && (isSignupVerificationFromStorage || waitingForAuthTokens)) {
    console.log('ProtectedRoute: Waiting for user during signup verification');
    return <LoadingFallback />;
  }

  // 1. First check if user needs to select account type
  if (!accountType && !isSetupRoute) {
    return <Navigate to="/role-selection" replace />;
  }

  // 2. Then check if user needs to complete onboarding
  if (accountType && !setupCompleted && !isSetupRoute) {
    return <Navigate to={`/onboarding?type=${accountType}`} replace />;
  }

  // 3. Only AFTER setup is complete, check beta access
  // Don't redirect to pending-approval if user is on setup routes (they need to complete setup first)
  const exemptRoutes = ['/terms', '/privacy'];
  const isExemptRoute = exemptRoutes.some(route => window.location.pathname.includes(route));
  
  if (setupCompleted && !betaAccess && !isExemptRoute && !isSetupRoute) {
    return <Navigate to="/pending-approval" replace />;
  }

  // User is authenticated and has account type set
  return <>{children}</>;
};